import { Link } from '@remix-run/react'
import { Check, Hexagon, Share2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Popup as MapboxPopup } from 'react-map-gl'
import { type FetchedPlot } from '#app/types'
import { placeDisplayName } from '#app/utils/place-name'
import { Button } from '../ui/button'

export type PopupContent = {
	latitude: number
	longitude: number
	handle: FetchedPlot['handle']
	note: FetchedPlot['note']
	user: FetchedPlot['user']
	isCluster?: boolean
	clusterCount?: number
	isClusterSelected?: boolean
}

export const Popup = ({
	popupContent,
	onClose,
	persistent = false,
	mobile = false,
}: {
	popupContent: PopupContent | null
	onClose: () => void
	persistent?: boolean
	mobile?: boolean
}) => {
	const [shareStatus, setShareStatus] = useState('')
	useEffect(() => {
		setShareStatus('')
	}, [popupContent?.handle])
	useEffect(() => {
		if (!shareStatus) return
		const timer = window.setTimeout(() => setShareStatus(''), 2500)
		return () => window.clearTimeout(timer)
	}, [shareStatus])
	useEffect(() => {
		if (!persistent) return
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose()
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [persistent, onClose])
	if (!popupContent || (mobile && !persistent)) return null
	const title = popupContent.isCluster
		? `${popupContent.clusterCount} ${popupContent.isClusterSelected ? 'selected' : 'owned'} places`
		: placeDisplayName(popupContent.handle, popupContent.note)
	const href = `/plot/${encodeURIComponent(popupContent.handle)}?from=map`
	const card = (
		<section
			className={`place-map-card ${persistent ? 'place-map-card-open' : ''}`}
			aria-label={title}
			onClick={event => event.stopPropagation()}
		>
			<div className="place-map-card-heading">
				<Hexagon
					aria-hidden="true"
					size={20}
					className="shrink-0 fill-purple-100 text-purple-600"
				/>
				<h2>{title}</h2>
				{persistent && (
					<button
						className="place-map-card-close"
						aria-label="Close place preview"
						onClick={onClose}
					>
						<X size={18} />
					</button>
				)}
			</div>
			{popupContent.user.handle && (
				<p className="place-map-card-owner">
					A place by @{popupContent.user.handle}
				</p>
			)}
			{persistent && (
				<>
					{popupContent.note && popupContent.note !== title && (
						<p className="place-map-card-story">{popupContent.note}</p>
					)}
					<div className="place-map-card-actions">
						<Button asChild size="sm">
							<Link to={href}>View place</Link>
						</Button>
						<Button
							size="sm"
							variant="outline"
							className="min-w-[96px]"
							onClick={async () => {
								try {
									await navigator.clipboard.writeText(
										`${window.location.origin}/plot/${encodeURIComponent(popupContent.handle)}`,
									)
									setShareStatus('Link copied')
								} catch {
									setShareStatus('Open the place to share its link')
								}
							}}
						>
							{shareStatus === 'Link copied' ? (
								<Check size={15} aria-hidden="true" />
							) : (
								<Share2 size={15} aria-hidden="true" />
							)}
							{shareStatus === 'Link copied'
								? 'Copied'
								: shareStatus
									? 'Try again'
									: 'Share'}
						</Button>
					</div>
					{shareStatus && (
						<p className="sr-only" role="status">
							{shareStatus}
						</p>
					)}
				</>
			)}
		</section>
	)
	if (mobile && persistent)
		return <div className="place-map-card-mobile">{card}</div>
	return (
		<MapboxPopup
			longitude={popupContent.longitude}
			latitude={popupContent.latitude}
			onClose={onClose}
			closeButton={false}
			closeOnClick={false}
			anchor="bottom"
			offset={24}
			maxWidth="340px"
			className={
				persistent ? 'place-popup place-popup-interactive' : 'place-popup'
			}
		>
			{card}
		</MapboxPopup>
	)
}
